import { Component, computed, inject, signal, OnInit } from '@angular/core';
import { DecimalPipe, formatDate } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { forkJoin } from 'rxjs';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { TableModule } from 'primeng/table';
import { ChartModule } from 'primeng/chart';
import { TooltipModule } from 'primeng/tooltip';
import { MiddlewareApi } from '../../core/services/middleware-api.service';
import { LayoutService } from '../../layout/service/layout.service';
import { SPEND_HINTS } from '../../core/courier-spend-hints';
import { DeliveryCostDetail, DeliveryCostReport } from '../../core/models';

// COURIER COSTS, PER STORE. The DoorDash admin page answers "is the integration wired"; this page
// answers the question a store manager brings — what did couriers cost each store — and answers it
// with orders:read, because reconciling a courier bill is order data, not an admin credential. Same
// two endpoints as that page's spend section (/costs and /costs/deliveries), same billing rules
// (committed deliveries only; voided — order refunded, order cancelled/closed, or the delivery itself
// cancelled with the courier — is shown but not billed),
// drawn as well as tabulated: the paired bars make an absorbed re-price VISIBLE as the gap between
// what DoorDash was paid and what shoppers were charged, which a column of near-identical numbers
// hides.

// The chart palette — validated data-viz slots stepped per surface, not the PrimeNG theme's accent
// ramp, which was never checked for colorblind separation or 3:1 contrast on either surface. Blue
// (paid) and orange (charged) clear the adjacent-pair CVD and normal-vision floors in both modes.
// "Not billed" is deliberately NEUTRAL gray, not a third hue: it is an excluded state, the same
// statement the delivery tables make by dimming voided rows.
const CHART_COLORS = {
    light: { paid: '#2a78d6', charged: '#eb6834', neutral: '#898781', grid: '#e1e0d9', ink: '#52514e', muted: '#898781', surface: '#ffffff' },
    dark: { paid: '#3987e5', charged: '#d95926', neutral: '#898781', grid: '#2c2c2a', ink: '#c3c2b7', muted: '#898781', surface: '#18181b' }
};

const CHART_FONT = 'system-ui, -apple-system, "Segoe UI", sans-serif';

@Component({
    selector: 'app-courier-costs',
    standalone: true,
    imports: [CardModule, ButtonModule, InputTextModule, MessageModule, TableModule, ChartModule, TooltipModule, FormsModule, DecimalPipe],
    template: `
        <p-card header="Courier costs by store">

            <div class="flex flex-wrap items-end gap-2 mb-3">
                <div class="flex flex-col gap-1">
                    <label for="cc-from" class="text-xs text-surface-500">From</label>
                    <input pInputText id="cc-from" type="date" [(ngModel)]="from" />
                </div>
                <div class="flex flex-col gap-1">
                    <label for="cc-to" class="text-xs text-surface-500">To</label>
                    <input pInputText id="cc-to" type="date" [(ngModel)]="to" />
                </div>
                <p-button label="Run" icon="pi pi-chart-bar" size="small" [loading]="loading()" (onClick)="load()" />
                <span class="text-xs text-surface-500">Empty dates read the last 30 days.</span>
            </div>

            @if (error()) { <p-message severity="error" styleClass="mb-3">{{ error() }}</p-message> }

            @if (report(); as r) {
                <!-- Separated, not pre-summed — the same totals line the DoorDash page shows, because
                     the quote cost, the driver's tip and what the shopper paid are three different money.
                     Each label carries its definition as a tooltip (SPEND_HINTS, shared with that page). -->
                <div class="flex flex-wrap gap-6 mb-3 text-sm">
                    <div><span class="text-surface-500 cursor-help" [pTooltip]="HINTS.fee">Quote fees</span>
                        <span class="font-semibold tabular-nums ml-1">{{ r.totalFees | number: '1.2-2' }}</span></div>
                    <div><span class="text-surface-500 cursor-help" [pTooltip]="HINTS.tip">Dasher tips</span>
                        <span class="font-semibold tabular-nums ml-1">{{ r.totalTips | number: '1.2-2' }}</span></div>
                    <div><span class="text-surface-500 cursor-help" [pTooltip]="HINTS.paid">Paid to DoorDash</span>
                        <span class="font-semibold tabular-nums ml-1">{{ r.totalPaidToCourier | number: '1.2-2' }}</span></div>
                    <div><span class="text-surface-500 cursor-help" [pTooltip]="HINTS.charged">Charged to shoppers</span>
                        <span class="font-semibold tabular-nums ml-1">{{ r.totalChargedToCustomers | number: '1.2-2' }}</span></div>
                    @if (r.totalAbsorbed !== 0) {
                        <div><span class="text-surface-500 cursor-help" [pTooltip]="HINTS.absorbed">Absorbed</span>
                            <span class="font-semibold tabular-nums ml-1" [class.text-orange-500]="r.totalAbsorbed > 0">
                                {{ r.totalAbsorbed | number: '1.2-2' }}</span></div>
                    }
                    @if (r.totalVoidedDeliveries > 0) {
                        <div><span class="text-surface-500 cursor-help" [pTooltip]="HINTS.billed">Not billed</span>
                            <span class="font-semibold tabular-nums ml-1">{{ r.totalVoidedDeliveries }}</span>
                            <span class="text-surface-500 ml-1">({{ r.totalVoidedAmount | number: '1.2-2' }})</span></div>
                    }
                </div>

                <p-table [value]="r.merchants" styleClass="p-datatable-sm">
                    <ng-template pTemplate="header">
                        <tr>
                            <th>Store</th><th>Deliveries</th>
                            <th class="cursor-help" [pTooltip]="HINTS.fee">Quote fees</th>
                            <th class="cursor-help" [pTooltip]="HINTS.tip">Tips</th>
                            <th class="cursor-help" [pTooltip]="HINTS.paid">Paid to DoorDash</th>
                            <th class="cursor-help" [pTooltip]="HINTS.charged">Charged</th>
                            <th class="cursor-help" [pTooltip]="HINTS.absorbed">Absorbed</th>
                            <th class="cursor-help" [pTooltip]="HINTS.billed">Not billed</th>
                        </tr>
                    </ng-template>
                    <ng-template pTemplate="body" let-m>
                        <tr>
                            <td>{{ m.merchantCode || m.locationId }}</td>
                            <td class="tabular-nums">{{ m.deliveries }}</td>
                            <td class="tabular-nums">{{ m.currency }} {{ m.fees | number: '1.2-2' }}</td>
                            <td class="tabular-nums">{{ m.tips | number: '1.2-2' }}</td>
                            <td class="tabular-nums font-semibold">{{ m.paidToCourier | number: '1.2-2' }}</td>
                            <td class="tabular-nums">{{ m.chargedToCustomers | number: '1.2-2' }}</td>
                            <td class="tabular-nums" [class.text-orange-500]="m.absorbed > 0">
                                @if (m.absorbed !== 0) { {{ m.absorbed | number: '1.2-2' }} } @else { &mdash; }
                            </td>
                            <td class="tabular-nums">
                                @if (m.voidedDeliveries > 0) {
                                    {{ m.voidedDeliveries }} <span class="text-surface-500">({{ m.voidedAmount | number: '1.2-2' }})</span>
                                } @else { &mdash; }
                            </td>
                        </tr>
                    </ng-template>
                    <ng-template pTemplate="emptymessage">
                        <tr><td colspan="8">No committed deliveries in this window.</td></tr>
                    </ng-template>
                </p-table>
            }
        </p-card>

        @if (report()) {
            <div class="grid grid-cols-1 xl:grid-cols-3 gap-4 mt-4">
                <div class="xl:col-span-2">
                    <p-card header="Paid vs charged, per store">
                        @if (storeChart(); as data) {
                            <p-chart type="bar" [data]="data" [options]="barOptions()" height="18rem" />
                        } @else {
                            <p class="text-sm text-surface-500 m-0">No committed deliveries in this window.</p>
                        }
                    </p-card>
                </div>
                <div>
                    <p-card header="Billed vs not billed">
                        @if (billedChart(); as data) {
                            <p-chart type="doughnut" [data]="data" [options]="doughnutOptions()" height="18rem" />
                        } @else {
                            <p class="text-sm text-surface-500 m-0">No deliveries in this window.</p>
                        }
                    </p-card>
                </div>
            </div>

            <p-card header="Paid per day" styleClass="mt-4">
                @if (dailyChart(); as data) {
                    <p-chart type="bar" [data]="data" [options]="dailyOptions()" height="14rem" />
                } @else {
                    <p class="text-sm text-surface-500 m-0">No billed deliveries in this window.</p>
                }
            </p-card>
        }
    `
})
export class CourierCosts implements OnInit {
    private readonly api = inject(MiddlewareApi);
    private readonly layout = inject(LayoutService);

    /** The spend glossary, one tooltip per label — shared with the DoorDash page so the wording cannot drift. */
    readonly HINTS = SPEND_HINTS;

    readonly report = signal<DeliveryCostReport | null>(null);
    readonly details = signal<DeliveryCostDetail[]>([]);
    readonly loading = signal(false);
    readonly error = signal<string | null>(null);
    from = '';
    to = '';

    /** The palette for the surface currently on screen. Charts rebuild when the theme flips —
     *  Chart.js paints to a canvas, so a repaint is the only way its colors follow the toggle. */
    private readonly colors = computed(() => (this.layout.isDarkTheme() ? CHART_COLORS.dark : CHART_COLORS.light));

    ngOnInit(): void {
        this.load();
    }

    load(): void {
        this.loading.set(true);
        this.error.set(null);
        const window = {
            from: this.from ? new Date(this.from).toISOString() : undefined,
            to: this.to ? new Date(this.to).toISOString() : undefined
        };
        // Both reads together: the totals and the per-day trail describe the same window, and loading
        // them separately lets one refresh while the other still shows the previous dates.
        forkJoin([this.api.getDeliveryCosts(window), this.api.getDeliveryCostDetails({ ...window, limit: 1000 })]).subscribe({
            next: ([report, details]) => {
                this.report.set(report);
                this.details.set(details);
                this.loading.set(false);
            },
            error: (e) => {
                this.loading.set(false);
                this.error.set(this.describe(e));
            }
        });
    }

    /** Paid vs charged per store — the absorbed cost is the visible gap between the pair. */
    readonly storeChart = computed(() => {
        const r = this.report();
        const c = this.colors();
        if (!r || r.merchants.length === 0) return null;
        return {
            labels: r.merchants.map((m) => m.merchantCode || m.locationId),
            datasets: [
                { label: 'Paid to DoorDash', data: r.merchants.map((m) => m.paidToCourier), backgroundColor: c.paid, borderRadius: 4, maxBarThickness: 28 },
                { label: 'Charged to shoppers', data: r.merchants.map((m) => m.chargedToCustomers), backgroundColor: c.charged, borderRadius: 4, maxBarThickness: 28 }
            ]
        };
    });

    /** Billed vs not-billed deliveries, as counts. Not-billed wears neutral gray on purpose — it is
     *  an excluded state, not a competing series (the tables dim those rows the same way). */
    readonly billedChart = computed(() => {
        const r = this.report();
        const c = this.colors();
        if (!r) return null;
        const billed = r.merchants.reduce((n, m) => n + m.deliveries, 0);
        if (billed + r.totalVoidedDeliveries === 0) return null;
        return {
            labels: ['Billed', 'Not billed'],
            datasets: [
                {
                    data: [billed, r.totalVoidedDeliveries],
                    backgroundColor: [c.paid, c.neutral],
                    // The 2px surface ring that keeps adjacent fills legible without an outline color.
                    borderColor: c.surface,
                    borderWidth: 2,
                    hoverOffset: 4
                }
            ]
        };
    });

    /** Paid per day, aggregated client-side from the per-delivery rows — keyed by the LOCAL day the
     *  delivery was accepted, the same day an operator reads off the details table, not the UTC day
     *  the wire timestamp happens to fall in. Billed deliveries only: this is the spend trail. */
    readonly dailyChart = computed(() => {
        const rows = this.details();
        const c = this.colors();
        const byDay = new Map<string, number>();
        for (const d of rows) {
            if (d.voided || !d.acceptedAt) continue;
            const day = formatDate(d.acceptedAt, 'yyyy-MM-dd', 'en-US');
            byDay.set(day, (byDay.get(day) ?? 0) + d.paidToCourier);
        }
        const days = [...byDay.keys()].sort();
        if (days.length === 0) return null;
        return {
            labels: days.map((d) => formatDate(d + 'T00:00:00', 'MMM d', 'en-US')),
            datasets: [{ label: 'Paid to DoorDash', data: days.map((d) => byDay.get(d)!), backgroundColor: c.paid, borderRadius: 4, maxBarThickness: 28 }]
        };
    });

    readonly barOptions = computed(() => this.cartesianOptions(true));

    /** One series needs no legend box — the card header names it. */
    readonly dailyOptions = computed(() => this.cartesianOptions(false));

    readonly doughnutOptions = computed(() => {
        const c = this.colors();
        return {
            maintainAspectRatio: false,
            cutout: '62%',
            plugins: {
                legend: { position: 'bottom', labels: { color: c.ink, usePointStyle: true, boxWidth: 8, font: { family: CHART_FONT } } }
            }
        };
    });

    /** Shared axis chrome: recessive grid, muted tick ink, y from zero (this is money, and a bar
     *  chart cut off above zero exaggerates every difference it shows). */
    private cartesianOptions(legend: boolean) {
        const c = this.colors();
        return {
            maintainAspectRatio: false,
            plugins: {
                legend: legend
                    ? { position: 'bottom', labels: { color: c.ink, usePointStyle: true, boxWidth: 8, font: { family: CHART_FONT } } }
                    : { display: false },
                tooltip: {
                    callbacks: {
                        label: (ctx: any) => `${ctx.dataset.label}: ${Number(ctx.parsed.y ?? 0).toFixed(2)}`
                    }
                }
            },
            scales: {
                x: {
                    ticks: { color: c.muted, font: { family: CHART_FONT } },
                    grid: { display: false },
                    border: { color: c.grid }
                },
                y: {
                    beginAtZero: true,
                    ticks: { color: c.muted, font: { family: CHART_FONT }, callback: (v: any) => (typeof v === 'number' ? v.toLocaleString() : v) },
                    grid: { color: c.grid },
                    border: { display: false }
                }
            }
        };
    }

    private describe(e: any): string {
        if (e?.status === 0) return 'Cannot reach the API.';
        if (e?.status === 403) return 'Forbidden — this page needs the orders:read permission (the same one the Orders page uses).';
        return e?.error?.message || `Error ${e?.status ?? ''}: ${e?.message ?? 'request failed'}`;
    }
}
